import React, { useEffect, useRef, useState, useCallback } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";

const APP_ID = process.env.REACT_APP_AGORA_APP_ID;
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ─── Token Fetcher ─────────────────────────────────────────────────────────────
// Both users request a "host" token — pure RTC mode, no audience
const fetchAgoraToken = async (channelName, uid) => {
  const url = `${API_BASE}/agora-token1/?channel=${encodeURIComponent(channelName)}&role=host&uid=${uid}`;
  const res = await fetch(url);
  const data = await res.json();
  if (!res.ok || !data.token) throw new Error(data.error || "Token fetch failed");
  return { token: data.token, appId: data.appId || APP_ID, uid: data.uid ?? uid };
};

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  root: { position: "fixed", inset: 0, background: "#0a0a0f", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: "'SF Pro Display',-apple-system,BlinkMacSystemFont,sans-serif", color: "#fff" },
  header: { position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", background: "linear-gradient(to bottom,rgba(0,0,0,0.7) 0%,transparent 100%)" },
  headerTitle: { fontSize: "15px", fontWeight: 600, letterSpacing: "0.02em", color: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", gap: "8px" },
  recordDot: { width: "8px", height: "8px", borderRadius: "50%", background: "#ff3b30", animation: "pulse 2s infinite" },
  headerRight: { fontSize: "13px", color: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.08)", padding: "5px 12px", borderRadius: "20px" },
  stage: { flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" },
  remoteWrapper: { position: "absolute", inset: 0, background: "#111118", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" },
  placeholder: { display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" },
  avatarRing: { width: "96px", height: "96px", borderRadius: "50%", background: "linear-gradient(135deg,#1c1c2e,#2d2d44)", border: "2px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "36px" },
  placeholderText: { fontSize: "15px", color: "rgba(255,255,255,0.35)", letterSpacing: "0.03em" },
  waitingDots: { display: "flex", gap: "5px", marginTop: "-8px" },
  dot: (d) => ({ width: "5px", height: "5px", borderRadius: "50%", background: "rgba(255,255,255,0.3)", animation: `bounce 1.4s ${d}s infinite ease-in-out` }),
  localWrapper: { position: "absolute", bottom: "100px", right: "24px", width: "200px", height: "130px", borderRadius: "12px", overflow: "hidden", background: "#1a1a28", border: "2px solid rgba(255,255,255,0.1)", boxShadow: "0 8px 32px rgba(0,0,0,0.6)", zIndex: 5, cursor: "grab", transition: "border-color 0.2s" },
  localVideoEl: { width: "100%", height: "100%" },
  localLabel: { position: "absolute", bottom: "6px", left: "8px", fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.7)", background: "rgba(0,0,0,0.5)", padding: "2px 8px", borderRadius: "10px" },
  camOffOverlay: { position: "absolute", inset: 0, background: "#1a1a28", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px" },
  remoteMuteBadge: { position: "absolute", top: "80px", left: "50%", transform: "translateX(-50%)", background: "rgba(255,59,48,0.85)", padding: "6px 16px", borderRadius: "20px", fontSize: "13px", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px", zIndex: 8, pointerEvents: "none" },
  controls: { position: "absolute", bottom: 0, left: 0, right: 0, height: "88px", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", background: "linear-gradient(to top,rgba(0,0,0,0.85) 0%,transparent 100%)", paddingBottom: "8px", zIndex: 10 },
  ctrlBtn: (active, danger) => ({ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px", background: danger ? "rgba(255,59,48,0.9)" : active ? "rgba(255,255,255,0.12)" : "rgba(255,59,48,0.12)", border: `1.5px solid ${danger ? "transparent" : active ? "rgba(255,255,255,0.15)" : "rgba(255,59,48,0.3)"}`, borderRadius: "50%", width: "52px", height: "52px", cursor: "pointer", transition: "all 0.15s ease", padding: 0, outline: "none" }),
  ctrlBtnWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" },
  ctrlLabelText: { fontSize: "11px", color: "rgba(255,255,255,0.45)", letterSpacing: "0.02em", marginTop: "2px" },
  statusBadge: (s) => ({ position: "absolute", top: "16px", left: "50%", transform: "translateX(-50%)", background: s === "connected" ? "rgba(52,199,89,0.85)" : s === "error" ? "rgba(255,59,48,0.85)" : "rgba(255,149,0,0.85)", padding: "5px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px", zIndex: 20, whiteSpace: "nowrap" }),
  screenShareBanner: { position: "absolute", top: "60px", left: "50%", transform: "translateX(-50%)", background: "rgba(10,132,255,0.85)", padding: "5px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 600, zIndex: 20, display: "flex", alignItems: "center", gap: "6px" },
  loadingScreen: { position: "fixed", inset: 0, background: "#0a0a0f", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "12px", color: "rgba(255,255,255,0.4)", fontSize: "14px" },
};

// ─── Icons ─────────────────────────────────────────────────────────────────────
const Icon = {
  Mic: () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>),
  MicOff: () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff3b30" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23" /><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" /><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>),
  Cam: () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>),
  CamOff: () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff3b30" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23" /><path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h2a2 2 0 0 1 2 2v9.34m-7.72-2.06a4 4 0 1 1-5.56-5.56" /></svg>),
  Phone: () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 4.43 9.88a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.32 10.9a16 16 0 0 0 3.36 2.41z" transform="rotate(135,12,12)" /></svg>),
  Screen: () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>),
};

// ─── Keyframes ─────────────────────────────────────────────────────────────────
const injectKeyframes = () => {
  if (document.getElementById("agora-meeting-kf")) return;
  const s = document.createElement("style");
  s.id = "agora-meeting-kf";
  s.textContent = `
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
    @keyframes bounce{0%,80%,100%{transform:scale(0);opacity:0.3}40%{transform:scale(1);opacity:1}}
    @keyframes fadeIn{from{opacity:0;transform:scale(0.97)}to{opacity:1;transform:scale(1)}}
    @keyframes spin{to{transform:rotate(360deg)}}
    .meeting-ctrl-btn:hover{transform:scale(1.08)!important;filter:brightness(1.2)}
    .meeting-ctrl-btn:active{transform:scale(0.95)!important}
    .pip-wrapper:hover{border-color:rgba(255,255,255,0.3)!important}
  `;
  document.head.appendChild(s);
};

// ─── Component ─────────────────────────────────────────────────────────────────
const MeetingRoom = () => {
  const { id: bookingId } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const location = useLocation();

const saved = JSON.parse(sessionStorage.getItem("prejoinSettings") || "{}");
/* ─────────────────────────────────────────────────────────
   CLOUD RECORDING
───────────────────────────────────────────────────────── */
const [recording, setRecording] = useState(false);
const [showStopDialog, setShowStopDialog] = useState(false);
const [recordingData, setRecordingData] = useState(null);

const recordingDataRef = useRef(null);
const isStoppingRef = useRef(false);

const recTimerRef = useRef(null);
const [recSeconds, setRecSeconds] = useState(0);
const micOn_state = location.state?.micOn ?? saved.micOn;
const camOn_state = location.state?.camOn ?? saved.camOn;
const userName = location.state?.userName ?? saved.userName;
const expertName = location.state?.expertName ?? saved.expertName;

  // ── Booking state ───────────────────────────────────────────────────────────
  const [booking, setBooking] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(true);

  // ── Recording refs ──────────────────────────────────────────────────────────
  // const mediaRecorderRef = useRef(null);
  // const recordedChunksRef = useRef([]);
  // const recordingStartedRef = useRef(false); // prevents duplicate start

  // ── Agora refs ──────────────────────────────────────────────────────────────
  const clientRef = useRef(null);
  const localTracksRef = useRef({ audio: null, video: null });
  const screenTrackRef = useRef(null);
  const mountedRef = useRef(true);
  const joinedRef = useRef(false);
  const initCalledRef = useRef(false);
  const remoteUserRef = useRef(null); // stable ref used inside recording closures

  // ── DOM refs ────────────────────────────────────────────────────────────────
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  // ── UI state ────────────────────────────────────────────────────────────────
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [tokenError, setTokenError] = useState(null);
  const [micOn, setMicOn] = useState(micOn_state !== undefined ? micOn_state : true);
  const [camOn, setCamOn] = useState(camOn_state !== undefined ? camOn_state : true);
  const [remoteUser, setRemoteUser] = useState(null);
  const [remoteMuted, setRemoteMuted] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // isHost determined from booking data only — no URL params, no props
  const isHost = booking ? String(booking.expert_id) === String(userId) : false;
  const remoteName = isHost ? userName : expertName;

  // ── Step 1: Fetch booking ───────────────────────────────────────────────────
  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const res = await fetch(`${API_BASE}/get_booking_details/?booking_id=${bookingId}`);
        if (!res.ok) throw new Error("Booking fetch failed");
        const data = await res.json();
        setBooking(data);
      } catch (err) {
        console.error("[Booking] fetch error:", err);
      } finally {
        setBookingLoading(false);
      }
    };
    if (bookingId) fetchBooking();
  }, [bookingId]);

  // ── Timer ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    injectKeyframes();
    const t = setInterval(() => { if (mountedRef.current) setElapsed(e => e + 1); }, 1000);
    return () => clearInterval(t);
  }, []);

 const formatRecTime = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

 /* ─────────────────────────────────────────────────────────
   START CLOUD RECORDING
───────────────────────────────────────────────────────── */
const startCloudRecording = useCallback(async () => {
  try {
    console.log("▶ Starting cloud recording");

    const res = await fetch(`${API_BASE}/start-booking-recording/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        channel: bookingId,
      }),
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const data = await res.json();

    console.log("✅ Recording started:", data);

    if (!data.resourceId || !data.sid) {
      console.error("❌ Missing resourceId or sid");
      return;
    }

    const rd = {
      resourceId: data.resourceId,
      sid: data.sid,
    };

    setRecordingData(rd);
    recordingDataRef.current = rd;

    setRecording(true);

    recTimerRef.current = setInterval(() => {
      setRecSeconds((s) => s + 1);
    }, 1000);

  } catch (err) {
    console.error("❌ startCloudRecording:", err);
  }
}, [bookingId]);


/* ─────────────────────────────────────────────────────────
   STOP CLOUD RECORDING
───────────────────────────────────────────────────────── */
const stopCloudRecording = useCallback(async () => {

  if (isStoppingRef.current) return;

  const rd = recordingDataRef.current;

  if (!rd?.resourceId || !rd?.sid) {
    console.error("❌ No recording data");
    return;
  }

  isStoppingRef.current = true;

  try {
    console.log("⏹ Stopping recording");

    const res = await fetch(`${API_BASE}/stop-booking-recording/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        resource_id: rd.resourceId,
        sid: rd.sid,
        channel: bookingId,
         booking_id: bookingId,
      }),
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    console.log("✅ Recording stopped");

    setRecording(false);
    setRecordingData(null);

    recordingDataRef.current = null;

    clearInterval(recTimerRef.current);
    recTimerRef.current = null;

    setRecSeconds(0);

  } catch (err) {
    console.error("❌ stopCloudRecording:", err);
    isStoppingRef.current = false;
  }
}, [bookingId]);

const handleStopRecording = async () => {
  try {
    await stopCloudRecording();

    // ✅ dialog auto closes
    setShowStopDialog(false);

  } catch (err) {
    console.error(err);

    // ✅ still close dialog on error
    setShowStopDialog(false);
  }
};

/* ─────────────────────────────────────────────────────────
   AUTO START RECORDING
───────────────────────────────────────────────────────── */
useEffect(() => {

  if (connectionStatus !== "connected") return;

  if (!isHost) return;

  const t = setTimeout(async () => {
    await startCloudRecording();
  }, 1500);

  return () => clearTimeout(t);

}, [connectionStatus, isHost, startCloudRecording]);

  // ── Recording ───────────────────────────────────────────────────────────────
  // const uploadRecording = useCallback(async (blob) => {
  //   try {
  //     console.log("[Recording] Uploading", (blob.size / 1024 / 1024).toFixed(2), "MB…");
  //     const formData = new FormData();
  //     formData.append("file", blob, `recording-${bookingId}-${Date.now()}.webm`);
  //     formData.append("booking_id", bookingId);
  //     const res = await fetch(`${API_BASE}/upload-meeting-recording/`, {
  //       method: "POST",
  //       body: formData,
  //     });
  //     if (!res.ok) throw new Error(`Upload failed: ${res.status} - ${await res.text()}`);
  //     const data = await res.json();
  //     console.log("[Recording] Uploaded →", data.url);
  //   } catch (err) {
  //     console.error("[Recording] Upload error:", err);
  //   }
  // }, [bookingId]);

  // const startRecording = useCallback(() => {
  //   // Hard guard — never start twice
  //   if (mediaRecorderRef.current || recordingStartedRef.current) {
  //     console.warn("[Recording] Already recording, skipping.");
  //     return;
  //   }

  //   try {
  //     const tracks = [];
  //     const { video: lv, audio: la } = localTracksRef.current;
  //     const remote = remoteUserRef.current;
  //     const remoteInitial = remoteName?.charAt(0)?.toUpperCase() || "U";

  //     if (lv?.getMediaStreamTrack) tracks.push(lv.getMediaStreamTrack());
  //     if (la?.getMediaStreamTrack) tracks.push(la.getMediaStreamTrack());
  //     if (remote?.videoTrack?.getMediaStreamTrack) tracks.push(remote.videoTrack.getMediaStreamTrack());
  //     if (remote?.audioTrack?.getMediaStreamTrack) tracks.push(remote.audioTrack.getMediaStreamTrack());

  //     if (tracks.length === 0) {
  //       console.warn("[Recording] No tracks available.");
  //       return;
  //     }

  //     const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
  //       ? "video/webm;codecs=vp9,opus"
  //       : "video/webm";

  //     const recorder = new MediaRecorder(new MediaStream(tracks), {
  //       mimeType,
  //       videoBitsPerSecond: 1_500_000,
  //     });
  //     recordedChunksRef.current = [];

  //     recorder.ondataavailable = (e) => {
  //       if (e.data?.size > 0) recordedChunksRef.current.push(e.data);
  //     };

  //     recorder.onstop = async () => {
  //       const blob = new Blob(recordedChunksRef.current, { type: mimeType });
  //       recordedChunksRef.current = [];
  //       recordingStartedRef.current = false;
  //       await uploadRecording(blob);
  //     };

  //     recorder.start(2000);
  //     mediaRecorderRef.current = recorder;
  //     recordingStartedRef.current = true;
  //     console.log("[Recording] Started | tracks:", tracks.length, "| mimeType:", mimeType);
  //   } catch (err) {
  //     console.error("[Recording] Failed to start:", err);
  //     recordingStartedRef.current = false;
  //   }
  // }, [uploadRecording]);

  // const stopRecording = useCallback(() => {
  //   const recorder = mediaRecorderRef.current;
  //   if (!recorder) return;
  //   try {
  //     if (recorder.state !== "inactive") {
  //       recorder.stop(); // triggers onstop → uploadRecording
  //       console.log("[Recording] Stopped.");
  //     }
  //   } catch (err) {
  //     console.error("[Recording] Stop error:", err);
  //   }
  //   mediaRecorderRef.current = null;
  // }, []);

  // Stable ref so Agora event closures (set up inside init) always have
  // the latest stopRecording without stale closure issues
//   const stopRecordingRef = useRef(stopRecording);
//   useEffect(() => { stopRecordingRef.current = stopRecording; }, [stopRecording]);

//   // ── Step 2: Agora init — runs once booking is loaded ───────────────────────
  useEffect(() => {
    if (bookingLoading || !booking) return;
    

    mountedRef.current = true;
    let cancelled = false;

    const init = async () => {
      try {
        const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        clientRef.current = client;

        // ── Agora events ───────────────────────────────────────────────────
      client.on("user-joined", (user) => {
  if (!mountedRef.current) return;

  console.log("User joined:", user.uid);
  remoteUserRef.current = user;
  setRemoteUser(user);
});

client.on("user-published", async (user, mediaType) => {
  if (cancelled || !mountedRef.current) return;

  try {
    await client.subscribe(user, mediaType);

    remoteUserRef.current = user;
    setRemoteUser(user);

    if (mediaType === "video" && remoteVideoRef.current) {
      user.videoTrack?.play(remoteVideoRef.current);
    }

    if (mediaType === "audio") {
      user.audioTrack?.play();
      setRemoteMuted(false);
    }

  } catch (err) {
    console.warn(err);
  }
});

        client.on("user-left", (user) => {
          if (!mountedRef.current) return;
          remoteUserRef.current = null;
          setRemoteUser(null);
          // Host stops + uploads recording when remote user leaves
         if (isHost) {
  console.log("⏹ Remote left → stopping cloud recording");
  stopCloudRecording();
}
        });

        // ── Token + join ───────────────────────────────────────────────────
        const uid = Math.floor(Math.random() * 100000);
        const { token, appId, uid: confirmedUid } = await fetchAgoraToken(bookingId, uid);

        if (cancelled) return;

        if (!token || appId !== APP_ID) {
          setTokenError("Invalid token or App ID mismatch");
          setConnectionStatus("error");
          return;
        }

        const joinUid = typeof confirmedUid === "number" ? confirmedUid : uid;
        await client.join(appId, bookingId, token, joinUid);
        joinedRef.current = true;

        if (cancelled || !mountedRef.current) {
          await client.leave().catch(() => {});
          return;
        }

        // ── Create + publish tracks ────────────────────────────────────────
        const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
          { encoderConfig: "music_standard" },
          { encoderConfig: { width: 1280, height: 720, frameRate: 30 } }
        );
        // ✅ APPLY PREJOIN STATE HERE
if (micOn_state === false) {
  await audioTrack.setEnabled(false);
  setMicOn(false);
}

if (camOn_state === false) {
  await videoTrack.setEnabled(false);
  setCamOn(false);
}

        if (cancelled || !mountedRef.current) {
          audioTrack.stop(); audioTrack.close();
          videoTrack.stop(); videoTrack.close();
          await client.leave().catch(() => {});
          return;
        }

        localTracksRef.current = { audio: audioTrack, video: videoTrack };
        if (localVideoRef.current && camOn_state !== false) videoTrack.play(localVideoRef.current);

        // Both users publish — pure RTC, no host/audience distinction
        await client.publish([audioTrack, videoTrack]);

        if (mountedRef.current) setConnectionStatus("connected");

      } catch (err) {
        if (!cancelled && mountedRef.current) {
          console.error("[Agora] init error:", err);
          setConnectionStatus("error");
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingLoading, booking]);

//   // ── Cleanup ─────────────────────────────────────────────────────────────────
  const cleanup = useCallback(async () => {

  const { audio, video } = localTracksRef.current;

  [audio, video].forEach((track) => {
    try {
      track?.stop();
      track?.close();
    } catch (_) {}
  });

  localTracksRef.current = {
    audio: null,
    video: null,
  };

  const screenTrack = screenTrackRef.current;

  if (screenTrack) {
    try {
      screenTrack.stop();
      screenTrack.close();
    } catch (_) {}

    screenTrackRef.current = null;
  }

  if (clientRef.current && joinedRef.current) {
    try {
      await clientRef.current.leave();
    } catch (_) {}

    joinedRef.current = false;
  }

}, []);
  // ── Controls ─────────────────────────────────────────────────────────────────
  const toggleMic = useCallback(async () => {
    const t = localTracksRef.current.audio;
    if (!t) return;
    try {
      await t.setEnabled(!micOn);
      if (mountedRef.current) setMicOn(v => !v);
    } catch (err) { console.warn("[Agora] mic toggle:", err); }
  }, [micOn]);

  
//   const toggleCam = useCallback(async () => {
//   const client = clientRef.current;
//   if (!client) return;

//   try {
//     if (camOn) {
//       // ── Turn OFF ──────────────────────────────────────────────────────
//       const oldTrack = localTracksRef.current.video;
//       if (oldTrack) {
//         try { await client.unpublish([oldTrack]); } catch (_) {}
//         oldTrack.stop();
//         oldTrack.close();
//         localTracksRef.current.video = null;
//       }
//       if (mountedRef.current) setCamOn(false);

//     } else {
//       // ── Turn ON — always create a fresh track ─────────────────────────
//       const newTrack = await AgoraRTC.createCameraVideoTrack({
//         encoderConfig: { width: 1280, height: 720, frameRate: 30 },
//       });
//       localTracksRef.current.video = newTrack;
//       await client.publish([newTrack]);
//       // Play AFTER publish to guarantee DOM binding is fresh
//       if (localVideoRef.current) newTrack.play(localVideoRef.current);
//       if (mountedRef.current) setCamOn(true);
//     }
//   } catch (err) {
//     console.warn("[Agora] cam toggle:", err);
//   }
// }, [camOn]);

const toggleCam = useCallback(async () => {
  const client = clientRef.current;
  if (!client) return;

  try {
    if (camOn) {
      const oldTrack = localTracksRef.current.video;
      if (oldTrack) {
        try { await client.unpublish([oldTrack]); } catch (_) {}
        oldTrack.stop();
        oldTrack.close();
        localTracksRef.current.video = null;
      }
      if (mountedRef.current) setCamOn(false);

    } else {
      const newTrack = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: { width: 1280, height: 720, frameRate: 30 },
      });
      localTracksRef.current.video = newTrack;
      await client.publish([newTrack]);

      // ✅ Set state FIRST so React mounts the video div
      if (mountedRef.current) setCamOn(true);
      // ✅ play() runs AFTER state update re-mounts localVideoRef
      // requestAnimationFrame gives React one frame to flush the DOM
      requestAnimationFrame(() => {
        if (localVideoRef.current) {
          newTrack.play(localVideoRef.current);
        }
      });
    }
  } catch (err) {
    console.warn("[Agora] cam toggle:", err);
  }
}, [camOn]);

useEffect(() => {
  if (!camOn) return;                          // camera is off, nothing to bind
  const track = localTracksRef.current.video;
  if (!track || !localVideoRef.current) return;

  // Clear any previous video element content before re-playing
  localVideoRef.current.innerHTML = "";
  track.play(localVideoRef.current);
}, [camOn]); // runs whenever camera turns on

// Helper — restores camera after screen share ends
const restoreCamera = useCallback(async () => {
  const client = clientRef.current;
  if (!client) return;
  try {
    const newCam = await AgoraRTC.createCameraVideoTrack({
      encoderConfig: { width: 1280, height: 720, frameRate: 30 },
    });
    localTracksRef.current.video = newCam;
    await client.publish([newCam]);
    if (localVideoRef.current) newCam.play(localVideoRef.current);
    if (mountedRef.current) {
      setCamOn(true);
      setScreenSharing(false);
    }
  } catch (err) {
    console.warn("[Agora] restore camera:", err);
  }
}, []);

const toggleScreenShare = useCallback(async () => {
  const client = clientRef.current;
  if (!client) return;

  try {
    if (!screenSharing) {
      // ── Start screen share ────────────────────────────────────────────
      // Step 1: tear down camera completely first
      const camTrack = localTracksRef.current.video;
      if (camTrack) {
        try { await client.unpublish([camTrack]); } catch (_) {}
        camTrack.stop();
        camTrack.close();
        localTracksRef.current.video = null;
      }

      // Step 2: create screen track
      const screenTrack = await AgoraRTC.createScreenVideoTrack(
        { encoderConfig: "1080p_1", optimizationMode: "detail" },
        "disable" // no separate audio track
      );
      screenTrackRef.current = screenTrack;

      // Step 3: publish + render
      await client.publish([screenTrack]);
      if (localVideoRef.current) screenTrack.play(localVideoRef.current);
      if (mountedRef.current) {
        setCamOn(false);      // camera is off while sharing
        setScreenSharing(true);
      }

      // Step 4: handle user clicking "Stop sharing" in the browser UI
      screenTrack.on("track-ended", async () => {
        const st = screenTrackRef.current;
        if (st) {
          try { await client.unpublish([st]); } catch (_) {}
          st.stop();
          st.close();
          screenTrackRef.current = null;
        }
        await restoreCamera(); // bring camera back
      });

    } else {
      // ── Stop screen share ─────────────────────────────────────────────
      const screenTrack = screenTrackRef.current;
      if (screenTrack) {
        try { await client.unpublish([screenTrack]); } catch (_) {}
        screenTrack.stop();
        screenTrack.close();
        screenTrackRef.current = null;
      }
      await restoreCamera(); // bring camera back
    }

  } catch (err) {
    // User cancelled the screen picker — restore camera silently
    console.warn("[Agora] screen share:", err);
    if (screenTrackRef.current) {
      try { screenTrackRef.current.stop(); screenTrackRef.current.close(); } catch (_) {}
      screenTrackRef.current = null;
    }
    await restoreCamera();
  }
}, [screenSharing, restoreCamera]);
  

  // Only host stops recording on leave — user just leaves
  const leaveCall = useCallback(async () => {

  mountedRef.current = false;

  try {

    if (isHost && recording) {
      await stopCloudRecording();
    }

    await cleanup();

  } catch (err) {
    console.error(err);
  }

  navigate(-1);

}, [
  cleanup,
  navigate,
  isHost,
  recording,
  stopCloudRecording
]);
  // ── Loading while booking fetches ───────────────────────────────────────────
  if (bookingLoading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={{ width: 28, height: 28, border: "2px solid #333", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        Loading session…
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          {connectionStatus === "connected" && isHost && <span style={styles.recordDot} />}
          Meeting Room
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>

  {recording && (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      background: "rgba(255,59,48,0.15)",
      border: "1px solid rgba(255,59,48,0.4)",
      padding: "4px 10px",
      borderRadius: 20,
      fontSize: 12,
      color: "#ff3b30",
      fontWeight: 600,
    }}>
      <div style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: "#ff3b30",
        animation: "pulse 1.2s infinite",
      }} />
      REC {formatRecTime(recSeconds)}
    </div>
  )}

  <div style={styles.headerRight}>
    {formatRecTime(elapsed)}
  </div>

</div>
      </div>

      {connectionStatus !== "connected" && (
        <div style={styles.statusBadge(connectionStatus)}>
          <span>{connectionStatus === "connecting" ? "⏳" : "⚠️"}</span>
          {connectionStatus === "connecting"
            ? "Connecting…"
            : tokenError ? `Error: ${tokenError}` : "Connection error"}
        </div>
      )}

      {screenSharing && (
        <div style={styles.screenShareBanner}>🖥️ You are sharing your screen</div>
      )}

      {remoteUser && remoteMuted && (
        <div style={styles.remoteMuteBadge}>🔇 Remote mic off</div>
      )}

      <div style={styles.stage}>
        <div style={styles.remoteWrapper}>
          {remoteUser ? (
            <div ref={remoteVideoRef} style={{ width: "100%", height: "100%", animation: "fadeIn 0.4s ease" }} />
          ) : (
            <div style={styles.placeholder}>
              <div style={styles.avatarRing}>👤</div>
              <div style={styles.placeholderText}>Waiting for participant</div>
              <div style={styles.waitingDots}>
                {[0, 0.2, 0.4].map((d, i) => <div key={i} style={styles.dot(d)} />)}
              </div>
            </div>
          )}
        </div>

        <div className="pip-wrapper" style={styles.localWrapper}>
          {!camOn && !screenSharing
            ? <div style={styles.camOffOverlay}>🎥</div>
            : <div ref={localVideoRef} style={styles.localVideoEl} />
          }
          <div style={styles.localLabel}>
            {isHost ? "You (Host)" : "You"}{!micOn && " 🔇"}
          </div>
        </div>
      </div>

      <div style={styles.controls}>
        <div style={styles.ctrlBtnWrap}>
          <button className="meeting-ctrl-btn" style={styles.ctrlBtn(micOn, false)} onClick={toggleMic}>
            {micOn ? <Icon.Mic /> : <Icon.MicOff />}
          </button>
          <span style={styles.ctrlLabelText}>{micOn ? "Mute" : "Unmute"}</span>
        </div>

        <div style={styles.ctrlBtnWrap}>
          <button className="meeting-ctrl-btn" style={styles.ctrlBtn(camOn, false)} onClick={toggleCam}>
            {camOn ? <Icon.Cam /> : <Icon.CamOff />}
          </button>
          <span style={styles.ctrlLabelText}>{camOn ? "Stop Video" : "Start Video"}</span>
        </div>

        <div style={styles.ctrlBtnWrap}>
          <button
            className="meeting-ctrl-btn"
            style={{
              ...styles.ctrlBtn(!screenSharing, false),
              background: screenSharing ? "rgba(10,132,255,0.85)" : "rgba(255,255,255,0.12)",
              border: screenSharing ? "1.5px solid rgba(10,132,255,0.5)" : "1.5px solid rgba(255,255,255,0.15)",
            }}
            onClick={toggleScreenShare}
          >
            <Icon.Screen />
          </button>
          <span style={styles.ctrlLabelText}>{screenSharing ? "Stop Share" : "Share"}</span>
        </div>
         {isHost && recording && (
  <div style={styles.ctrlBtnWrap}>
    <button
      className="meeting-ctrl-btn"
      style={{
        ...styles.ctrlBtn(true, true),
        background: "rgba(255,59,48,0.9)",
      }}
      onClick={() => setShowStopDialog(true)}
    >
      ⏺
    </button>

    <span style={styles.ctrlLabelText}>
      Stop Rec
    </span>
  </div>
)}
        <div style={styles.ctrlBtnWrap}>
          <button
            className="meeting-ctrl-btn"
            style={{ ...styles.ctrlBtn(true, true), background: "rgba(255,59,48,0.9)", border: "1.5px solid rgba(255,59,48,0.5)" }}
            onClick={leaveCall}
          >
            <Icon.Phone />
          </button>
          <span style={styles.ctrlLabelText}>Leave</span>
        </div>
      </div>
      {/* ─────────────────────────────────────────────────────
    STOP RECORDING DIALOG
───────────────────────────────────────────────────── */}
{showStopDialog && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.65)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
    }}
  >
    <div
      style={{
        width: 360,
        background: "#111118",
        borderRadius: 18,
        padding: 24,
        border: "1px solid rgba(255,255,255,0.08)",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          marginBottom: 12,
        }}
      >
        Stop Recording?
      </div>

      <div
        style={{
          fontSize: 13,
          color: "rgba(255,255,255,0.6)",
          marginBottom: 22,
          lineHeight: 1.5,
        }}
      >
        This will permanently stop the cloud recording.
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "center",
        }}
      >
        {/* ✅ Cancel auto closes */}
        <button
          onClick={() => setShowStopDialog(false)}
          style={{
            padding: "10px 18px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.1)",
            background: "transparent",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>

        {/* ✅ Yes auto closes */}
        <button
          onClick={handleStopRecording}
          style={{
            padding: "10px 18px",
            borderRadius: 10,
            border: "none",
            background: "#ff3b30",
            color: "#fff",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Yes, Stop
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default MeetingRoom;